/*  ========================================================================
    # Sections - Image Gallery
    ========================================================================  */

import React, { useEffect, useRef, useState } from 'react';
import type { ImageGalleryProps } from '.';
import { useSwipeable } from 'react-swipeable';
import clsx from 'clsx';
import useSVGSprite from '@/react/hooks/useSVGSprite';

const ImageGallery = ({ headline, images }: ImageGalleryProps) => {
  const imageGalleryWrapperRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const svgSprite = useSVGSprite();

  /**
   * Calculate the number of columns based on the container width.
   */
  const columnCount = () => {
    const container = imageGalleryWrapperRef.current;
    if (container === null) return 3;
    const containerWidth = container.offsetWidth;
    if (containerWidth < 600) return 1;
    if (containerWidth < 900) return 2;
    return 3;
  };

  /**
   * Grid spacing.
   */
  const gridSpacing = () => {
    const container = imageGalleryWrapperRef.current;
    if (container === null) return 40;
    const containerWidth = container.offsetWidth;
    if (containerWidth < 600) return 20;
    return 40;
  };

  /**
   * Calculate the positions of the images in the grid layout.
   * This function is called after all images are loaded.
   */
  const calcEntries = () => {
    const container = imageGalleryWrapperRef.current;
    if (container) {
      const items = Array.from(container.children) as HTMLElement[];
      const columnHeights = Array(columnCount()).fill(0);
      const columnWidth = ((container.offsetWidth - (columnCount() - 1) * 40) / columnCount());

      items.forEach(item => {
        const minCol = columnHeights.indexOf(Math.min(...columnHeights));
        const x = minCol * columnWidth + ((minCol) > -1 ? (minCol) * 40 : 0);
        const y = columnHeights[minCol];

        item.style.top = `${y}px`;
        item.style.left = `${x}px`;
        item.style.width = `${columnWidth}px`;

        columnHeights[minCol] += item.offsetHeight + gridSpacing();
        container.style.height = `${Math.max(...columnHeights)}px`;
      });
    };
  };

  /**
   * Navigate to the next image in the lightbox.
   */
  const lightBoxNavRight = () => {
    if (activeImage < images.length - 1) setActiveImage(activeImage + 1);
  };

  /**
   * Navigate to the previous image in the lightbox.
   */
  const lightBoxNavLeft = () => {
    if (activeImage > 0) setActiveImage(activeImage - 1);
  };

  /**
   * Swipe handlers.
   */
  const swipeHandlers = useSwipeable({
    onSwipedRight: lightBoxNavLeft,
    onSwipedLeft: lightBoxNavRight
  });

  /**
   * Effect to calculate the grid layout and handle image loading.
   * This effect runs once when the component mounts and when the window is resized.
   */
  useEffect(() => {
    const container = imageGalleryWrapperRef.current;
    if (!container) return;

    const images = Array.from(container.querySelectorAll('img'));
    let loadedCount = 0;

    const onImageLoad = () => {
      loadedCount += 1;
      if (loadedCount === images.length) {
        calcEntries();
      }
    };

    images.forEach(img => {
      if (img.complete) {
        onImageLoad();
      } else {
        img.addEventListener('load', onImageLoad);
        img.addEventListener('error', onImageLoad);
      }
    });

    // Handle window resize.
    let currentContainerWidth = container.offsetWidth;
    window.addEventListener('resize', () => {
      const newContainerWidth = container.offsetWidth;
      if (newContainerWidth === currentContainerWidth) return;
      currentContainerWidth = newContainerWidth;
      container.style.height = 'auto';
      calcEntries();
    });
  }, []);

  /**
   * Effect to handle body scroll lock, focus management, and ESC key event when lightbox is open.
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isLightboxOpen) setIsLightboxOpen(false);
    };

    const toggleTabIndex = (disable: boolean) => {
      const focusableElements = document.querySelectorAll<HTMLElement>(
        'body *:not(.s-image-gallery__lightbox *)'
      );

      focusableElements.forEach(element => {
        if (disable) {
          element.setAttribute('tabindex', '-1');
        } else {
          element.removeAttribute('tabindex');
        }
      });
    };

    if (isLightboxOpen) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      toggleTabIndex(true);
      document.body.style.overflow = 'hidden';
      closeButtonRef.current?.focus();
      window.addEventListener('keydown', handleKeyDown);
    } else {
      // Restore page scrolling
      document.body.style.overflow = '';
      toggleTabIndex(false);
      previouslyFocusedElement.current?.focus();
      window.removeEventListener('keydown', handleKeyDown);
    }

    // Cleanup function to ensure proper state management
    return () => {
      toggleTabIndex(false);
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen]);

  return (
    <>
      <div className='container'>
        {/* Headline */}
        {headline ? (
          <div className='s-image-gallery__headline'>
            <div className='prose' dangerouslySetInnerHTML={{ __html: headline }} />
          </div>
        ) : null}

        {/* Image Gallery */}
        <div className='s-image-gallery__wrapper' ref={imageGalleryWrapperRef}>
          {images.map((src, index) => (
            <button
              key={index}
              className='s-image-gallery__image'
              onClick={() => {
                setIsLightboxOpen(true);
                setActiveImage(index);
              }}
            >
              <figure>
                <img src={src.sizes.large} alt={src.alt} />
              </figure>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <div
        className={clsx('s-image-gallery__lightbox', { 's-image-gallery__lightbox--active': isLightboxOpen })}
        onClick={event => {
          if (event.target === event.currentTarget) {
            setIsLightboxOpen(false);
          }
        }}
      >
        <button
          ref={closeButtonRef}
          className='s-image-gallery__lightbox-close'
          onClick={() => setIsLightboxOpen(false)}
          type='button'
          aria-label='Lightbox schließen'
          tabIndex={isLightboxOpen ? 0 : -1}
        >
          <svg>
            <use href={`${svgSprite}#close`} />
          </svg>
        </button>
        <div className='s-image-gallery__lightbox-content'>
          <button
            className={clsx(
              's-image-gallery__lightbox-nav',
              { 's-image-gallery__lightbox-nav--disabled': activeImage === 0 }
            )}
            onClick={lightBoxNavLeft}
            type='button'
            aria-label='Zum vorherigen Bild navigieren'
            tabIndex={isLightboxOpen && activeImage !== 0 ? 0 : -1}
          >
            <svg>
              <use href={`${svgSprite}#chevron-left`} />
            </svg>
          </button>
          <div
            className='s-image-gallery__lightbox-images'
            {...swipeHandlers}
          >
            {images.map((src, index) => (
              <figure key={index} data-active={activeImage === index ? 1 : 0}>
                <img src={src.url} alt={src.alt} loading='lazy' />
                {src.caption ? <figcaption>{src.caption}</figcaption> : null}
              </figure>
            ))}
          </div>
          <button
            className={clsx(
              's-image-gallery__lightbox-nav',
              { 's-image-gallery__lightbox-nav--disabled': activeImage === images.length - 1 }
            )}
            onClick={lightBoxNavRight}
            type='button'
            aria-label='Zum nächsten Bild navigieren'
            tabIndex={isLightboxOpen && activeImage !== images.length - 1 ? 0 : -1}
          >
            <svg>
              <use href={`${svgSprite}#chevron-right`} />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default ImageGallery;
